'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Upload, Check, ChevronRight, ChevronLeft, AlertCircle, FileText, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/context/RoleContext';
import { useRouter } from 'next/navigation';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  password: string;
  confirmPassword: string;
  program: string;
  sessionPreference: string;
  financialAid: boolean;
  agreeTerms: boolean;
  agreeHipaa: boolean;
}

interface Program {
  name: string;
  abbr: string;
  color: string;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  programs: Program[];
}

const steps = [
  { id: 1, label: 'Personal Info' },
  { id: 2, label: 'Program Selection' },
  { id: 3, label: 'Documents & Agreement' },
];

const uploadedDocs: Array<{ id: string; name: string; required: boolean; uploaded: boolean }> = [
  { id: 'doc-gov-id', name: 'Government-issued Photo ID', required: true, uploaded: false },
  { id: 'doc-hs-diploma', name: 'High School Diploma / GED', required: true, uploaded: false },
  { id: 'doc-immunization', name: 'Immunization Records', required: true, uploaded: false },
  { id: 'doc-bg-check', name: 'Background Check Authorization', required: true, uploaded: false },
  { id: 'doc-health-exam', name: 'Physical Health Exam Form', required: false, uploaded: false },
  { id: 'doc-tb-test', name: 'TB Test Results', required: false, uploaded: false },
];

export default function RegisterForm({ onSwitchToLogin, programs }: RegisterFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState(uploadedDocs);
  const { signUp } = useAuth();
  const { setRole } = useRole();
  const router = useRouter();

  const { register, handleSubmit, watch, trigger, formState: { errors }, setError } = useForm<RegisterFormData>({
    defaultValues: { financialAid: false, agreeTerms: false, agreeHipaa: false }
  });

  const selectedProgram = watch('program');
  const password = watch('password');

  const handleDocUpload = (docId: string) => {
    setDocuments(prev => prev.map(d => d.id === docId ? { ...d, uploaded: true } : d));
    toast.success('Document uploaded successfully');
  };

  const handleDocRemove = (docId: string) => {
    setDocuments(prev => prev.map(d => d.id === docId ? { ...d, uploaded: false } : d));
  };

  const nextStep = async () => {
    let fieldsToValidate: (keyof RegisterFormData)[] = [];
    if (currentStep === 1) fieldsToValidate = ['firstName', 'lastName', 'email', 'phone', 'dob', 'password', 'confirmPassword'];
    if (currentStep === 2) fieldsToValidate = ['program', 'sessionPreference'];

    const valid = await trigger(fieldsToValidate);
    if (valid) setCurrentStep(s => Math.min(s + 1, 3));
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        full_name: `${data.firstName} ${data.lastName}`,
        role: 'applicant',
        phone: data.phone,
        program: data.program,
      });
      setRole('applicant');
      toast.success('Application submitted! Redirecting to your dashboard...');
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      const msg = err?.message || 'Registration failed';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        setError('email', { message: 'This email is already registered. Please sign in.' });
        setCurrentStep(1);
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display font-700 text-gray-900 text-2xl mb-1">Apply for Enrollment</h2>
        <p className="text-gray-500 text-sm">Complete all steps to submit your student application</p>
        <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p className="text-xs text-blue-700">This form is for <strong>student enrollment only</strong>. Teacher and administrator accounts are created exclusively by school administrators.</p>
        </div>
      </div>

      {/* Step progress */}
      <div className="flex items-center gap-2 mb-7">
        {steps.map((step, idx) => (
          <React.Fragment key={`step-${step.id}`}>
            <div className="flex items-center gap-1.5">
              <div className={`
                w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all
                ${currentStep > step.id ? 'bg-teal-600 text-white' : currentStep === step.id ? 'bg-teal-700 text-white ring-4 ring-teal-100' : 'bg-gray-200 text-gray-500'}
              `}>
                {currentStep > step.id ? <Check size={12} /> : step.id}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${currentStep === step.id ? 'text-teal-700' : 'text-gray-400'}`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 rounded-full ${currentStep > step.id ? 'bg-teal-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Personal Info */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-base">First Name <span className="text-red-500">*</span></label>
                <input
                  className={`input-base ${errors.firstName ? 'border-red-400' : ''}`}
                  placeholder="Maria"
                  {...register('firstName', { required: 'Required' })}
                />
                {errors.firstName && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.firstName.message}</p>}
              </div>
              <div>
                <label className="label-base">Last Name <span className="text-red-500">*</span></label>
                <input
                  className={`input-base ${errors.lastName ? 'border-red-400' : ''}`}
                  placeholder="Gonzalez"
                  {...register('lastName', { required: 'Required' })}
                />
                {errors.lastName && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label className="label-base">Email Address <span className="text-red-500">*</span></label>
              <input
                type="email"
                className={`input-base ${errors.email ? 'border-red-400' : ''}`}
                placeholder="maria@email.com"
                {...register('email', { required: 'Required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Valid email required' } })}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.email.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-base">Phone <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  className={`input-base ${errors.phone ? 'border-red-400' : ''}`}
                  placeholder="(617) 555-0142"
                  {...register('phone', { required: 'Required' })}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.phone.message}</p>}
              </div>
              <div>
                <label className="label-base">Date of Birth <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  className={`input-base ${errors.dob ? 'border-red-400' : ''}`}
                  {...register('dob', { required: 'Required' })}
                />
                {errors.dob && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.dob.message}</p>}
              </div>
            </div>

            <div>
              <label className="label-base">Password <span className="text-red-500">*</span></label>
              <p className="text-xs text-gray-400 mb-1.5">At least 8 characters with one uppercase, one number</p>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`input-base pr-10 ${errors.password ? 'border-red-400' : ''}`}
                  placeholder="••••••••"
                  {...register('password', {
                    required: 'Required',
                    minLength: { value: 8, message: 'Minimum 8 characters' },
                    pattern: { value: /^(?=.*[A-Z])(?=.*\d)/, message: 'Must include uppercase letter and number' }
                  })}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.password.message}</p>}
            </div>

            <div>
              <label className="label-base">Confirm Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`input-base pr-10 ${errors.confirmPassword ? 'border-red-400' : ''}`}
                  placeholder="••••••••"
                  {...register('confirmPassword', {
                    required: 'Required',
                    validate: val => val === password || 'Passwords do not match'
                  })}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.confirmPassword.message}</p>}
            </div>
          </div>
        )}

        {/* Step 2: Program Selection */}
        {currentStep === 2 && (
          <div className="space-y-5">
            <div>
              <label className="label-base">Select Your Program <span className="text-red-500">*</span></label>
              <p className="text-xs text-gray-400 mb-3">Choose the certification program you want to enroll in</p>
              <div className="grid grid-cols-1 gap-2">
                {programs.map(prog => (
                  <label key={`prog-select-${prog.abbr}`} className={`
                    flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all
                    ${selectedProgram === prog.name ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300 bg-white'}
                  `}>
                    <input type="radio" value={prog.name} {...register('program', { required: 'Please select a program' })} className="sr-only" />
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${prog.color}`}>
                      {prog.abbr.slice(0, 3)}
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-gray-800">{prog.name}</span>
                      <span className="text-xs text-gray-400 ml-2">({prog.abbr})</span>
                    </div>
                    {selectedProgram === prog.name && <Check size={16} className="text-teal-600" />}
                  </label>
                ))}
              </div>
              {errors.program && <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.program.message}</p>}
            </div>

            <div>
              <label className="label-base">Session Preference <span className="text-red-500">*</span></label>
              <select
                className={`input-base ${errors.sessionPreference ? 'border-red-400' : ''}`}
                {...register('sessionPreference', { required: 'Please select a session' })}
              >
                <option value="">Select a session...</option>
                <option value="morning">Morning (8:00 AM – 12:00 PM)</option>
                <option value="afternoon">Afternoon (1:00 PM – 5:00 PM)</option>
                <option value="evening">Evening (6:00 PM – 9:30 PM)</option>
                <option value="weekend">Weekend (Sat–Sun, 9:00 AM – 3:00 PM)</option>
                <option value="online">Online / Self-paced</option>
              </select>
              {errors.sessionPreference && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.sessionPreference.message}</p>}
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <input
                type="checkbox"
                id="financialAid"
                {...register('financialAid')}
                className="mt-0.5 w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <label htmlFor="financialAid" className="text-sm font-semibold text-amber-800 cursor-pointer">I am interested in financial aid options</label>
                <p className="text-xs text-amber-600 mt-0.5">Our financial aid coordinator will contact you with available payment plans, scholarships, and workforce grants.</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Documents & Agreement */}
        {currentStep === 3 && (
          <div className="space-y-5">
            <div>
              <label className="label-base">Required Documents</label>
              <p className="text-xs text-gray-400 mb-3">Upload your documents. Required documents must be submitted before enrollment is finalized.</p>
              <div className="space-y-2">
                {documents.map(doc => (
                  <div key={doc.id} className={`
                    flex items-center gap-3 p-3 rounded-xl border transition-all
                    ${doc.uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}
                  `}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${doc.uploaded ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                      {doc.uploaded ? <Check size={14} className="text-emerald-600" /> : <FileText size={14} className="text-gray-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.name}</p>
                      <p className="text-xs text-gray-400">{doc.required ? 'Required' : 'Optional'} {doc.uploaded ? '· Uploaded' : ''}</p>
                    </div>
                    {doc.uploaded ? (
                      <button
                        type="button"
                        onClick={() => handleDocRemove(doc.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove document"
                      >
                        <X size={14} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleDocUpload(doc.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors"
                      >
                        <Upload size={12} /> Upload
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-xl border border-gray-200">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  {...register('agreeTerms', { required: 'You must agree to the terms' })}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <label htmlFor="agreeTerms" className="text-sm text-gray-700 cursor-pointer">
                  I agree to the <span className="text-teal-600 underline">Terms of Enrollment</span> and <span className="text-teal-600 underline">Privacy Policy</span>. I understand the refund and withdrawal policies.
                </label>
              </div>
              {errors.agreeTerms && <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.agreeTerms.message}</p>}

              <div className="flex items-start gap-3 p-3 rounded-xl border border-gray-200">
                <input
                  type="checkbox"
                  id="agreeHipaa"
                  {...register('agreeHipaa', { required: 'HIPAA acknowledgment is required' })}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <label htmlFor="agreeHipaa" className="text-sm text-gray-700 cursor-pointer">
                  I acknowledge the <span className="text-teal-600 underline">HIPAA Confidentiality Agreement</span> and understand my obligations regarding patient privacy during clinical training.
                </label>
              </div>
              {errors.agreeHipaa && <p className="text-xs text-red-600 flex items-center gap-1"><AlertCircle size={11} />{errors.agreeHipaa.message}</p>}
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-7">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={() => setCurrentStep(s => s - 1)}
              className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl hover:bg-gray-50 transition-all active:scale-95"
            >
              <ChevronLeft size={15} /> Back
            </button>
          )}
          {currentStep < 3 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-teal-700 hover:bg-teal-800 text-white font-display font-semibold text-sm rounded-xl transition-all active:scale-95"
            >
              Continue <ChevronRight size={15} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-teal-700 hover:bg-teal-800 text-white font-display font-semibold text-sm rounded-xl transition-all active:scale-95 disabled:opacity-70 disabled:scale-100"
            >
              {isLoading ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</>
              ) : 'Submit Application'}
            </button>
          )}
        </div>
      </form>

      <p className="text-center text-sm text-gray-500 mt-5">
        Already enrolled?{' '}
        <button onClick={onSwitchToLogin} className="text-teal-600 font-semibold hover:underline">Sign in here</button>
      </p>
    </div>
  );
}